import { clsx } from "clsx";
import { useEffect, useRef, useState } from "react";

import { loadEnv } from "@smart/bridge-env-dom";
import { PaymentResponse } from "@smart/bridge-types-basic";
import { useAsync } from "@smart/itops-hooks-dom";
import { loadLocale } from "@smart/itops-locale-dom";
import { Icon, Loading } from "@smart/itops-sb-design-system-dom";
import { backoff, Env, isTestEnv, Region } from "@smart/itops-utils-basic";

import { ErrorDisplay } from "./error";
import { Label } from "./label";
import { FieldProps, FieldWrapper } from "./wrapper";
import { LoadResponses } from "../../types";

const paymentPortalOrigins = {
  aus: {
    local: isTestEnv() ? "https://test.smartsb.dev" : "https://dev.smartsb.dev",
    dev: "https://devpayments-billing.smokeball.com.au",
    staging: "https://rcpayments-billing.smokeball.com.au",
    live: "https://payments-billing.smokeball.com.au",
  },
  nova: {
    local: isTestEnv() ? "https://test.smartsb.dev" : "https://dev.smartsb.dev",
    dev: "https://devpayments-billing.smokeball.com",
    staging: "https://rcpayments-billing.smokeball.com",
    live: "https://payments-billing.smokeball.com",
  },
  lon: {
    local: isTestEnv() ? "https://test.smartsb.dev" : "https://dev.smartsb.dev",
    dev: "https://devpayments-billing.smokeball.co.uk",
    staging: "https://rcpayments-billing.smokeball.co.uk",
    live: "https://payments-billing.smokeball.co.uk",
  },
} as const;

type ChargeDataMessage = {
  messageType: "CHARGE_DATA_READY";
  paymentRequestId: string;
  payload: {
    chargeRequest: PaymentResponse["chargeRequest"];
  };
};

export const PaymentField = ({
  field,
  index,
  value,
  error,
  loading,
  disabled,
  showPaymentPlaceholder,
  onChange,
  onBlur,
  loadResponses,
}: FieldProps<HTMLDivElement, PaymentResponse> & {
  loadResponses: LoadResponses;
}) => {
  const frame = useRef<HTMLIFrameElement>(null);
  const [paymentResponse, setPaymentResponse] = useState<
    PaymentResponse | undefined
  >(value);

  const isSetupIncomplete =
    !field.payment?.amountInCents || !field.payment.bankAccount;

  useAsync(async () => {
    if (
      paymentResponse?.paymentPortalUrl ||
      isSetupIncomplete ||
      showPaymentPlaceholder
    )
      return;

    const getPaymentPortalUrl = (
      responses: Awaited<ReturnType<LoadResponses>>,
    ) => responses?.[field.uri]?.value?.paymentPortalUrl;

    const responses = await backoff({
      run: loadResponses,
      test: (found) => !!getPaymentPortalUrl(found),
      attempts: 5,
      base: 1000,
      shouldCatch: true,
    })();

    if (getPaymentPortalUrl(responses)) {
      setPaymentResponse(responses?.[field.uri].value);
    }
  }, [paymentResponse?.paymentPortalUrl]);

  useEffect(() => {
    const region = loadEnv("Region") as Region;
    const env = loadEnv("Env") as Env;
    const listener = (event: MessageEvent<ChargeDataMessage>) => {
      const { data } = event;
      const isValid =
        data.messageType === "CHARGE_DATA_READY" &&
        data.paymentRequestId === paymentResponse?.linkId;
      if (
        event.origin === paymentPortalOrigins[region][env] &&
        paymentResponse &&
        isValid
      ) {
        onChange({
          ...paymentResponse,
          chargeRequest: data.payload.chargeRequest,
        });
        onBlur();
      }
    };
    window.addEventListener("message", listener);

    return () => window.removeEventListener("message", listener);
  }, [paymentResponse]);

  if (isSetupIncomplete) return null;

  const isLoading =
    !showPaymentPlaceholder && (loading || !paymentResponse?.paymentPortalUrl);

  return (
    <FieldWrapper disabled={disabled}>
      <Label {...field} />
      <div className="rounded border border-neutral-200 mt-[0.8rem] mb-[0.4rem] overflow-hidden">
        {!showPaymentPlaceholder &&
          !value?.chargeRequest &&
          paymentResponse?.paymentPortalUrl && (
            <iframe
              className={clsx(
                "w-full border-none",
                loadLocale().country === "US"
                  ? "min-h-[117rem]"
                  : "min-h-[89rem]",
              )}
              ref={frame}
              title="Payment Portal"
              data-testid="payment-portal"
              src={paymentResponse.paymentPortalUrl}
            />
          )}
        <div className="flex justify-start items-center gap-small text-ui px-[2.4rem] py-[1.6rem]">
          {value?.chargeRequest ? (
            <Icon
              className="text-green-800"
              name="solidCircleCheck"
              dataTestId="check-icon"
            />
          ) : (
            <Icon className="text-blue-660" name="solidCircleInfo" />
          )}
          {showPaymentPlaceholder
            ? "Payment field is not visible in preview mode."
            : "Payment will be processed upon submission of this form."}
        </div>
        {isLoading && <Loading size="small" />}
      </div>
      <ErrorDisplay uri={field.uri} index={index} error={error} />
    </FieldWrapper>
  );
};
